import type { Media } from '../../lib/flotiq-client/types/api-response';

import { use, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../lib/helpers';

import UserContext from '../../contexts/UserContext';

import { CheckmarkIcon, DownloadIcon } from '../../images/shapes';

import Button from '../Button/Button';
import Card from '../Card/Card';
import Loader from '../Loader/Loader';

const unsplashCardDefaultPropMediaData = {
  fileName: 'Unknown',
  size: 'Unknown',
  source: 'Unknown',
  sourceUrl: '#',
  author: 'Unknown',
  authorUrl: '#',
  links: { html: '#' },
  urls: { thumb: '#' },
  user: { links: { html: '#' } },
} as unknown as Media;

interface UnsplashCardProps {
  /**
   * Object with image data
   */
  mediaData?: Media;
  /**
   * Callback for saving unsplash media
   */
  saveMediaCallback?: (id: string) => Promise<boolean>;
  /**
   * Card test id
   */
  testId?: string;
}

const UnsplashCard = ({
  mediaData = unsplashCardDefaultPropMediaData,
  saveMediaCallback,
  testId = '',
}: UnsplashCardProps) => {
  const { t } = useTranslation();
  const { permissions } = use(UserContext);

  const { canCreate } = useMemo(
    () => permissions.getCoPermissions('_media') || {},
    [permissions],
  );

  const imageUrl = useMemo(() => mediaData.urls.thumb, [mediaData]);

  const [isSaving, setIsSaving] = useState(false);
  const [locked, setLocked] = useState(false);

  const saveImage = useCallback(async () => {
    setIsSaving(true);
    setLocked(true);
    await saveMediaCallback?.(mediaData.id);
    setLocked(false);
    setIsSaving(false);
  }, [mediaData, saveMediaCallback]);

  const btnIcon = useMemo(() => {
    if (locked) {
      return (
        <CheckmarkIcon
          className="w-4 text-blue"
          title={t('Media.MediaCard.SavedMediaBtn')}
        />
      );
    }
    return (
      <DownloadIcon
        className="w-4 text-blue"
        title={t('Media.MediaCard.SaveMediaBtn')}
      />
    );
  }, [locked, t]);

  const imageOverlay = useMemo(() => {
    return (
      <div className="flex flex-col h-full p-3 justify-end">
        <div className="flex justify-end transition duration-200 ease-in-out p-1 space-x-2">
          {canCreate && (
            <Button
              testId={`${testId}-save-media-btn`}
              additionalClasses={twMerge(
                'bg-white dark:bg-[#0B1E39]',
                'border-blue',
                'hover:bg-white',
                'hover:border-white dark:hover:border-slate-800',
                'disabled:bg-white',
              )}
              buttonSize="sm"
              iconColor="black"
              iconImage={
                isSaving ? (
                  <Loader
                    size={'small'}
                    type={'spinner-grid'}
                    testId={`${testId}-media-loading`}
                  />
                ) : (
                  btnIcon
                )
              }
              disabled={isSaving || locked}
              onClick={saveImage}
            />
          )}
        </div>
      </div>
    );
  }, [btnIcon, canCreate, isSaving, locked, saveImage, testId]);

  return (
    <Card
      testId={testId}
      body={
        <div className="w-full h-full p-2 text-xs sm:text-sm bg-white dark:bg-slate-950 font-light sm:p-4 ">
          <div className="flex flex-col mt-2">
            <div className="text-sm">
              <div
                {...getTestProps(testId, 'image-author')}
                className="dark:text-white"
              >
                {t('Media.MediaCard.ImageData.UnsplashAuthor')}
                <Link
                  to={mediaData.user.links.html}
                  className="text-blue whitespace-nowrap"
                  target={'_blank'}
                  rel="noreferrer"
                >
                  {mediaData.user.name}
                </Link>
              </div>
              <div
                {...getTestProps(testId, 'image-source')}
                className="dark:text-white"
              >
                {t('Media.MediaCard.ImageData.UnsplashSource')}
                <Link
                  to={mediaData.links.html}
                  className="text-blue whitespace-nowrap"
                  target={'_blank'}
                  rel="noreferrer"
                >
                  {t('Media.MediaCard.Unsplash')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      }
      imageOverlay={imageOverlay}
      imageUrl={imageUrl}
      imageUrlAlt={mediaData.fileName}
      additionalContainerClasses="w-auto border border-slate-100 dark:border-none"
      additionalImageClasses="object-scale-down h-32 w-auto sm:h-52 dark:bg-slate-700"
      additionalBodyClasses="h-full"
      additionalImageContainerClasses="h-32 w-auto sm:h-52 dark:bg-slate-700"
    />
  );
};

export default UnsplashCard;
